import React, { Suspense, useMemo, useState } from 'react'

import { Trans } from '@lingui/react/macro'
import { useLocation, useNavigate } from 'react-router-dom'

import { XStack, styled, View } from 'tamagui'

import { Text } from '../Text'
import { Link } from '../Link'
import { Spinner } from '../Spinner'
import { Group } from '../Group'
import { Icon } from '../Icon'

const ROUTES = ['/guilds', '/events', '/presentations'] as const

export const DynamicIslandHomeExpand = () => {
  const location = useLocation()

  const activeIndex = useMemo(() => {
    const index = ROUTES.findIndex((route) =>
      location.pathname.startsWith(route)
    )
    return index >= 0 ? index : undefined
  }, [location.pathname])

  const activeRoute =
    activeIndex !== undefined ? ROUTES[activeIndex] : undefined

  const [itemHovered, setItemHovered] = useState<
    (typeof ROUTES)[number] | undefined
  >()

  const isIconActive = (route: (typeof ROUTES)[number]) =>
    activeRoute === route || itemHovered === route

  return (
    <Group
      activeIndex={activeIndex}
      gap='$2'
      highlightedElementProps={{
        borderRadius: '$6',
        backgroundColor: '$backgroundAccent',
      }}
      marginHorizontal={-4}
    >
      <Group.Item index={0}>
        <Link
          href='/guilds'
          hoverStyle={{ textDecorationLine: 'none' }}
          padding='$1'
          paddingRight='$5'
          onMouseEnter={() => setItemHovered('/guilds')}
          onMouseLeave={() => setItemHovered(undefined)}
        >
          <XStack gap='$3' alignItems='center'>
            <StyledIconWrapper
              backgroundColor={
                isIconActive('/guilds')
                  ? '$backgroundPress'
                  : '$backgroundStrong'
              }
            >
              <StyledIcon name='people' />
            </StyledIconWrapper>

            <Text>
              <Trans>Guilds</Trans>
            </Text>
          </XStack>
        </Link>
      </Group.Item>

      <Group.Item index={1}>
        <Link
          href='/events'
          hoverStyle={{ textDecorationLine: 'none' }}
          padding='$1'
          paddingRight='$5'
          onMouseEnter={() => setItemHovered('/events')}
          onMouseLeave={() => setItemHovered(undefined)}
        >
          <XStack gap='$3' alignItems='center'>
            <StyledIconWrapper
              backgroundColor={
                isIconActive('/events')
                  ? '$backgroundPress'
                  : '$backgroundStrong'
              }
            >
              <StyledIcon name='calendar' />
            </StyledIconWrapper>

            <Text>
              <Trans>Events</Trans>
            </Text>
          </XStack>
        </Link>
      </Group.Item>

      <Group.Item index={2}>
        <Link
          href='/presentations'
          hoverStyle={{ textDecorationLine: 'none' }}
          padding='$1'
          paddingRight='$5'
          onMouseEnter={() => setItemHovered('/presentations')}
          onMouseLeave={() => setItemHovered(undefined)}
        >
          <XStack gap='$3' alignItems='center'>
            <StyledIconWrapper
              backgroundColor={
                isIconActive('/presentations')
                  ? '$backgroundPress'
                  : '$backgroundStrong'
              }
            >
              <StyledIcon name='people-community' />
            </StyledIconWrapper>

            <Text>
              <Trans>Presentations</Trans>
            </Text>
          </XStack>
        </Link>
      </Group.Item>
    </Group>
  )
}

const StyledIconWrapper = styled(View, {
  animation: 'fastSpring',
  alignItems: 'center',
  justifyContent: 'center',
  // @ts-expect-error - borderRadius '50%' is a web-only value
  borderRadius: '50%',
  backgroundColor: '$backgroundStrong',
  width: '$6',
  height: '$6',
})

const StyledIcon = styled(Icon, {
  size: '$5',
  padding: 0,
})
